// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Stepper } from '@mantine/core';
import { Stack, Group, Center } from '@/components/ui/layout';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { TextInput, PasswordInput, Checkbox, Select } from '@/components/ui/inputs';
import { IconUser, IconMail, IconLock, IconArrowLeft, IconArrowRight, IconBuilding, IconSparkles } from '@tabler/icons-react';
import { AuthLayoutWrapper, PasswordStrength } from '../components';

export default function RegisterModern() {
  const [active, setActive] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState<string | null>(null);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const nextStep = () => setActive((current) => Math.min(current + 1, 2));
  const prevStep = () => setActive((current) => Math.max(current - 1, 0));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Register attempt:', { name, email, password, company, role });
  };

  return (
    <AuthLayoutWrapper variant="modern" maxWidth="sm">
      <Stack gap="lg">
        {/* Back button */}
        <Button
          component={Link}
          to="/app/demo/auth-pages/register"
          variant="subtle"
          size="xs"
          leftSection={<IconArrowLeft size={14} />}
          w="fit-content"
        >
          Back to variants
        </Button>

        {/* Header */}
        <Stack align="center" gap="xs">
          <Badge
            leftSection={<IconSparkles size={12} />}
            variant="gradient"
            gradient={{ from: 'teal', to: 'lime' }}
            size="lg"
          >
            Get Started Free
          </Badge>
          <Title order={2} ta="center" mt="sm">Create your account</Title>
          <Text c="dimmed" size="sm" ta="center">
            Complete these steps to set up your profile
          </Text>
        </Stack>

        {/* Stepper */}
        <Stepper active={active} size="sm">
          <Stepper.Step label="Account" description="Basic info">
            <Stack gap="md" mt="lg">
              <TextInput
                label="Full name"
                placeholder="John Doe"
                leftSection={<IconUser size={16} />}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                size="md"
                radius="md"
              />

              <TextInput
                label="Email address"
                placeholder="john@example.com"
                leftSection={<IconMail size={16} />}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                size="md"
                radius="md"
              />
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Security" description="Set password">
            <Stack gap="md" mt="lg">
              <PasswordInput
                label="Password"
                placeholder="Create a strong password"
                leftSection={<IconLock size={16} />}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                size="md"
                radius="md"
              />
              {password && (
                <PasswordStrength password={password} variant="requirements" />
              )}
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Profile" description="About you">
            <Stack gap="md" mt="lg">
              <TextInput
                label="Company name"
                placeholder="Your company"
                leftSection={<IconBuilding size={16} />}
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                size="md"
                radius="md"
              />

              <Select
                label="Your role"
                placeholder="Select your role"
                value={role}
                onChange={setRole}
                data={[
                  { value: 'developer', label: 'Developer' },
                  { value: 'designer', label: 'Designer' },
                  { value: 'manager', label: 'Manager' },
                  { value: 'other', label: 'Other' },
                ]}
                size="md"
                radius="md"
              />

              <Checkbox
                label={
                  <Text size="sm">
                    I agree to the <Anchor size="sm">Terms</Anchor> and{' '}
                    <Anchor size="sm">Privacy Policy</Anchor>
                  </Text>
                }
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.currentTarget.checked)}
              />
            </Stack>
          </Stepper.Step>
        </Stepper>

        {/* Navigation Buttons */}
        <Group justify="space-between" mt="md">
          {active > 0 ? (
            <Button variant="outline" onClick={prevStep}>
              Back
            </Button>
          ) : (
            <div />
          )}

          {active < 2 ? (
            <Button onClick={nextStep} rightSection={<IconArrowRight size={16} />}>
              Continue
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              variant="gradient"
              gradient={{ from: 'teal', to: 'lime' }}
              disabled={!acceptTerms}
            >
              Create account
            </Button>
          )}
        </Group>

        {/* Login Link */}
        <Text ta="center" size="sm" mt="md">
          Already have an account?{' '}
          <Anchor component={Link} to="/app/demo/auth-pages/login/modern" fw={500} c="teal">
            Sign in
          </Anchor>
        </Text>
      </Stack>
    </AuthLayoutWrapper>
  );
}
